import MUIStack from "@mui/material/Stack";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import moment from "moment";
import QuickFilter from "../../Seating/Filters/quickFilter";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import InfoItem from "../InfoItem";
import ReportSetting from "../ReportSetting";

export default class ReportSettingDateRange extends ReportSetting {
  handlePresetChange = (e, value) => {
    if (value) {
      const dates = QuickFilter.getDates(value.value);

      this.handleSettingChange(
        "beginDate",
        moment(dates[0])
          .startOf("day")
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      );
      this.handleSettingChange(
        "endDate",
        moment(dates[1])
          .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      );
      this.handleSettingChange("preset", value);
    } else {
      this.handleSettingChange("beginDate", null);
      this.handleSettingChange("endDate", null);
      this.handleSettingChange("preset", null);
    }
  };

  handleDateChange = (key, value) => {
    if (value) {
      if (key === "endDate") {
        this.handleSettingChange(
          key,
          moment(value)
            .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        );
      } else {
        this.handleSettingChange(
          key,
          moment(value)
            .startOf("day")
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        );
      }

      this.handleSettingChange("preset", { label: "Custom", value: 9 });
    } else {
      this.handleSettingChange(key, null);
      this.handleSettingChange("preset", null);
    }
  };

  /**
   * Renders a begin date selector.
   */
  renderBeginDatePicker() {
    return (
      <MUIDatePicker
        label={"Begin Date"}
        value={
          this.state.settings.beginDate
            ? moment(this.state.settings.beginDate)
            : null
        }
        sx={{ width: "300px" }}
        slotProps={{
          field: {
            clearable: true,
          },
          textField: {
            size: "small",
            variant: "standard",
            required: true,
            disabled: this.props.presetOnly,
            error:
              this.props.showValidationIssues &&
              !!this.props.validationIssues?.beginDate,
            helperText:
              this.props.showValidationIssues &&
              this.props.validationIssues?.beginDate
                ? this.props.validationIssues?.beginDate
                : "",
            InputLabelProps: {
              shrink: true,
            },
          },
        }}
        onChange={(date) => this.handleDateChange("beginDate", date)}
        views={["year", "month", "day"]}
      />
    );
  }
  /**
   * Renders an end date selector.
   */
  renderEndDatePicker() {
    return (
      <MUIDatePicker
        label={"End Date"}
        value={
          this.state.settings.endDate
            ? moment(this.state.settings.endDate)
            : null
        }
        sx={{ width: "300px" }}
        slotProps={{
          field: {
            clearable: true,
          },
          textField: {
            size: "small",
            variant: "standard",
            required: true,
            disabled: this.props.presetOnly,
            error:
              this.props.showValidationIssues &&
              !!this.props.validationIssues?.endDate,
            helperText:
              this.props.showValidationIssues &&
              this.props.validationIssues?.endDate
                ? this.props.validationIssues?.endDate
                : "",
            InputLabelProps: {
              shrink: true,
            },
          },
        }}
        onChange={(date) => this.handleDateChange("endDate", date)}
        views={["year", "month", "day"]}
      />
    );
  }
  /**
   * Renders a preset dropdown selector.
   */
  renderPresetDropdown() {
    return (
      <MUIAutocomplete
        options={QuickFilter.getOptions().filter((option) => {
          if (this.props.presetList && this.props.presetList.length > 0) {
            return this.props.presetList.includes(option.label.toLowerCase());
          }
          return true;
        })}
        isOptionEqualToValue={(option, value) => {
          return option.value === value.value;
        }}
        value={this.state.settings.preset || null}
        onChange={this.handlePresetChange}
        renderInput={(params) => {
          return (
            <MUITextField
              {...params}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: "300px" }}
              label="Preset"
              variant="standard"
              fullWidth={true}
            />
          );
        }}
      />
    );
  }

  renderContent() {
    return (
      <MUIStack direction="row" spacing={2} alignItems="center">
        {this.renderBeginDatePicker()}
        {this.renderEndDatePicker()}
        {this.renderPresetDropdown()}
      </MUIStack>
    );
  }

  renderReadOnly() {
    if (!this.state.settings.beginDate && !this.state.settings.endDate) {
      return <InfoItem label="Date Range" value="None" />;
    }

    // Determine date format based on dateViews prop
    let dateFormat = "MMM D, YYYY"; // default: full date

    if (this.props.dateViews) {
      if (
        this.props.dateViews.length === 1 &&
        this.props.dateViews[0] === "year"
      ) {
        dateFormat = "YYYY"; // year only
      } else if (
        this.props.dateViews.length === 2 &&
        this.props.dateViews.includes("year") &&
        this.props.dateViews.includes("month")
      ) {
        dateFormat = "MMM YYYY"; // month and year
      }
      // Otherwise use default full date format
    }

    let dateRangeText;
    if (this.state.settings.beginDate && this.state.settings.endDate) {
      // Both dates set: show range
      dateRangeText = `${moment(this.state.settings.beginDate).format(dateFormat)} - ${moment(this.state.settings.endDate).format(dateFormat)}`;
    } else if (this.state.settings.beginDate) {
      // Only begin date set: show "from" date
      dateRangeText = `From ${moment(this.state.settings.beginDate).format(dateFormat)}`;
    } else {
      // Only end date set: show "through" date
      dateRangeText = `Through ${moment(this.state.settings.endDate).format(dateFormat)}`;
    }

    return <InfoItem label="Date Range" value={dateRangeText} />;
  }
}
